import React, { useCallback, useContext, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormSetError } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import {
  convertFileUrlToFileName,
  renderDateString,
  zodKeys,
} from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useListApi } from "@/hooks/useListApi";
import { DynamicSelector } from "../DynamicSelector";
import { Pleading, pleadingApi } from "@/api/pleading";
import { Case } from "@/api/cases";
import { FaPaperclip } from "react-icons/fa6";
import { ImCross } from "react-icons/im";
import { CustomToolTip } from "../CustomToolTip";
import { useTranslation } from "react-i18next";
import { DateFormField } from "@/components/custom/DateFormField";
import { useOptimisticLocking } from "@/hooks/useOptimisticLocking";
import { ConflictResolutionDialog } from "@/components/ui/conflict-resolution-dialog";
import {
  withVersion,
  createOptimisticLockingToasts,
} from "@/lib/optimisticLockingHelpers";
import { pleadingFieldConfig } from "@/lib/fieldDiffUtils";

export function AddUpdatePleading({
  open,
  setOpen,
  pleading,
  trigger,
  refresh,
  reviewCase,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pleading?: Pleading;
  trigger?: JSX.Element;
  refresh?: () => Promise<void>;
  reviewCase: Case;
}) {
  const { t } = useTranslation();
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;
  const { loading, user } = contextValues;
  const { setLoading } = set;

  const toasts = createOptimisticLockingToasts(
    t("pleadings.title.pleading"),
    t,
  );

  const {
    loading: pleadingLoading,
    search: pleadingSearch,
    loadNext: loadNextPleading,
    list: pleadingList,
  } = useListApi<Pleading>({
    baseApiObject: pleadingApi,
    enable: open,
    defaultParams: { case: reviewCase.id },
    additionalListIds: [pleading?.associated_pleading?.id.toString()],
  });

  const FormSchema = z.object({
    case_id: z.string().min(1, t("common.statictext.field_required")),
    user_id: z.number().min(1, t("common.statictext.field_required")),
    associated_pleading_id: z.string().nullable().optional(),
    title: z.string().min(1, t("common.statictext.field_required")),
    party: z.string().min(1, t("common.statictext.field_required")),
    description: z.string().min(1, t("common.statictext.field_required")),
    type: z.string().min(1, t("common.statictext.field_required")),
    filing_date: z.date().nullable().optional(),
    response_necessary: z.boolean().default(false),
    due_date: z.date().nullable().optional(),
    associated_pleading: z.string().nullable().optional(),
    file: z.instanceof(File).or(z.string()).nullable().optional(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      case_id: reviewCase.id.toString(),
      user_id: user?.user.id,
      associated_pleading_id: null,
      title: "",
      party: "",
      description: "",
      type: "",
      response_necessary: false,
      filing_date: null,
      due_date: null,
      file: null,
      associated_pleading: "",
    },
  });

  // Function to get current form data for conflict comparison
  const getCurrentFormData = useCallback(
    (version: number): Pleading => {
      const formData = form.getValues();
      return {
        id: pleading?.id,
        version: version,
        case_id: parseInt(formData.case_id),
        title: formData.title,
        party: formData.party,
        description: formData.description,
        type: formData.type,
        filing_date: formData.filing_date?.toLocaleDateString("sv") || null,
        response_necessary: formData.response_necessary || false,
        due_date: formData.due_date
          ? formData.due_date.toLocaleDateString("sv")
          : null,
        associated_pleading_id: formData.associated_pleading_id
          ? parseInt(formData.associated_pleading_id)
          : null,
        file: formData.file,
      } as Pleading;
    },
    [form, pleading?.id],
  );

  // Optimistic locking hook
  const {
    currentVersion,
    updateVersion,
    updateWithLocking,
    conflictState,
    handleRefresh,
    handleForceOverwrite,
    clearConflict,
  } = useOptimisticLocking<Pleading>({
    initialVersion: pleading?.version || 0,
    resourceId: pleading?.id || 0,
    resourceName: t("pleadings.title.pleading"),
    apiRead: (id) =>
      pleadingApi.read({
        id: id.toString(),
        token: user?.access,
      }),
    apiUpdate: (id, data) =>
      pleadingApi.update({
        id: id.toString() + "/",
        data,
        token: user?.access,
        headers: {
          ...(data.file && typeof data.file !== "string"
            ? { "content-type": "multipart/form-data" }
            : {}),
        },
      }),
    onUpdateSuccess: () => {
      toast(toasts.updateSuccess);
    },
    onConflict: () => {
      toast(toasts.conflictDetected);
    },
    onUpdateError: (error) => {
      toast(toasts.error(error));
    },
    diffConfig: pleadingFieldConfig,
    localData: pleading ? getCurrentFormData(pleading.version) : undefined,
  });

  const errorHandler = useApiErrorHandler("AddUpdatePleading", {
    setFormError: form.setError as UseFormSetError<Record<string, unknown>>,
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      setLoading?.(true);
      if (pleading && pleading.id) {
        // UPDATE with optimistic locking
        const result = await updateWithLocking(
          withVersion(
            {
              case_id: parseInt(data.case_id),
              user_id: user?.user.id,
              title: data.title,
              party: data.party,
              description: data.description,
              type: data.type,
              filing_date: data.filing_date?.toLocaleDateString("sv"),
              due_date: data.due_date
                ? data.due_date.toLocaleDateString("sv")
                : null,
              response_necessary: data.response_necessary,
              updated_by: user?.user.id,
              associated_pleading_id: data.associated_pleading_id
                ? parseInt(data.associated_pleading_id)
                : undefined,
              ...(typeof data.file === "string" ? {} : { file: data.file }),
            },
            currentVersion,
          ),
        );

        if (result.conflict || !result.success) return;
      } else {
        await pleadingApi
          .create({
            url: "/",
            headers: {
              "content-type": "multipart/form-data",
            },
            data: {
              case_id: parseInt(data.case_id),
              user_id: user?.user.id,
              title: data.title,
              party: data.party,
              description: data.description,
              type: data.type,
              filing_date: data.filing_date?.toLocaleDateString("sv"),
              due_date: data.due_date?.toLocaleDateString("sv"),
              response_necessary: data.response_necessary,
              updated_by: user?.user.id,
              associated_pleading_id: data.associated_pleading_id
                ? parseInt(data.associated_pleading_id)
                : undefined,
              ...(typeof data.file === "string" ? {} : { file: data.file }),
            },
            token: user?.access,
          })
          .then(() => {
            toast({
              title: t("common.toast.added_successfully", {
                resource: t("pleadings.title.pleading"),
              }),
            });
            form.reset();
          });
      }
      await refresh?.();
      setOpen(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    if (pleading) {
      updateVersion(pleading.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...pleading,
            case_id: reviewCase.id.toString(),
            filing_date: pleading.filing_date
              ? new Date(renderDateString(pleading.filing_date, true))
              : pleading.filing_date,
            due_date: pleading.due_date
              ? new Date(renderDateString(pleading.due_date, true))
              : pleading.due_date,
            associated_pleading_id: pleading.associated_pleading
              ? pleading.associated_pleading.id.toString()
              : undefined,
            associated_pleading: undefined, // pop it out
            case: undefined, // pop it out
            user: undefined, // pop it out
            user_id: user?.user.id,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pleading, reviewCase, user]);

  useEffect(() => {
    if (reviewCase && open) {
      form.setValue("case_id", reviewCase.id.toString(), { shouldTouch: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewCase, open]);

  const buttonAction = pleading?.id
    ? t("common.button.update")
    : t("common.button.add");

  // Conflict resolution handlers
  const handleRefreshPleading = async () => {
    if (!pleading?.id) return;
    const refreshed = await handleRefresh();
    if (refreshed) {
      // Update form with refreshed data
      updateVersion(refreshed.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...refreshed,
            case_id: reviewCase.id.toString(),
            filing_date: refreshed.filing_date
              ? new Date(renderDateString(refreshed.filing_date, true))
              : refreshed.filing_date,
            due_date: refreshed.due_date
              ? new Date(renderDateString(refreshed.due_date, true))
              : refreshed.due_date,
            associated_pleading_id: refreshed.associated_pleading
              ? refreshed.associated_pleading.id.toString()
              : undefined,
            associated_pleading: undefined,
            case: undefined,
            user: undefined,
            user_id: user?.user.id,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
      toast(toasts.refreshSuccess);
    }
  };

  const handleForceOverwritePleading = async () => {
    if (!conflictState.latestVersion || !pleading?.id) return;
    const formData = form.getValues();
    const result = await handleForceOverwrite(
      withVersion(
        {
          case_id: parseInt(formData.case_id),
          user_id: user?.user.id,
          title: formData.title,
          party: formData.party,
          description: formData.description,
          type: formData.type,
          filing_date: formData.filing_date?.toLocaleDateString("sv"),
          due_date: formData.due_date
            ? formData.due_date.toLocaleDateString("sv")
            : null,
          response_necessary: formData.response_necessary,
          updated_by: user?.user.id,
          associated_pleading_id: formData.associated_pleading_id
            ? parseInt(formData.associated_pleading_id)
            : undefined,
          ...(typeof formData.file === "string" ? {} : { file: formData.file }),
        },
        conflictState.latestVersion,
      ),
    );
    if (result.success) {
      toast(toasts.overwriteSuccess);
      await refresh?.();
      setOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {!!trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          className="w-full md:w-[500px] max-h-screen overflow-y-auto"
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full flex flex-col gap-8"
            >
              <DialogHeader>
                <DialogTitle>
                  {buttonAction} {t("pleadings.title.pleading")}
                </DialogTitle>
              </DialogHeader>

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.title")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("common.placeholder.title")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="party"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("pleadings.label.party")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("pleadings.placeholder.party")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.description")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("common.placeholder.description")}
                        className="resize-none"
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.type")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("pleadings.placeholder.type")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="filing_date"
                render={({ field }) => (
                  <DateFormField
                    control={form.control}
                    name="filing_date"
                    label={t("pleadings.label.filing_date")}
                    description={t("pleadings.statictext.filing_date")}
                    required={true}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="file"
                render={({ field }) => {
                  let fileName = null;
                  if (typeof field.value === "string") {
                    fileName = convertFileUrlToFileName(field.value);
                  }
                  return (
                    <FormItem>
                      <FormLabel>{t("common.label.file")}</FormLabel>
                      {fileName && (
                        <div className="flex gap-2 items-center">
                          <FaPaperclip color="skyblue" />
                          <a
                            href={field.value as string}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-primary break-all"
                          >
                            {fileName}
                          </a>
                          <CustomToolTip
                            trigger={
                              <ImCross
                                className="cursor-pointer"
                                onClick={() => {
                                  form.setValue("file", null);
                                }}
                                color="red"
                              />
                            }
                            tip={t("common.statictext.remove_file")}
                          />
                        </div>
                      )}
                      <FormControl>
                        <Input
                          type="file"
                          {...field}
                          value={undefined}
                          onChange={(event) =>
                            form.setValue(
                              "file",
                              event.target.files ? event.target.files[0] : null,
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t("common.statictext.upload_files")}
                        <p className="text-sm text-info">
                          {t("common.statictext.upload_files_detail")}
                        </p>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="associated_pleading_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("pleadings.label.associated_pleading")}{" "}
                      {pleadingLoading
                        ? t("common.button.loading_with_parens")
                        : ""}
                    </FormLabel>
                    <DynamicSelector
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                      options={pleadingList
                        .filter((item) =>
                          pleading?.id ? pleading.id !== item.id : true,
                        )
                        .map((item) => ({
                          label: item.title,
                          value: item.id.toString(),
                        }))}
                      className="selector"
                      onLoadNext={loadNextPleading}
                      onSearch={(searchValue) => {
                        pleadingSearch.set(searchValue);
                      }}
                      placeholder={t(
                        "pleadings.placeholder.associated_pleading",
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="response_necessary"
                render={({ field }) => (
                  <div>
                    <FormItem className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        {t("pleadings.label.response_necessary")}
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                    <FormDescription className="pl-6">
                      {t("pleadings.statictext.response_necessary")}
                    </FormDescription>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <DateFormField
                    control={form.control}
                    name="due_date"
                    label={t("pleadings.label.due_date")}
                    description={t("pleadings.statictext.due_date")}
                  />
                )}
              />
              <div className="flex flex-col">
                <FormLabel>{t("common.label.case")}</FormLabel>
                <b>{reviewCase.name}</b>
              </div>
              <DialogFooter>
                <Button disabled={loading} type="submit">
                  {loading ? t("common.button.loading") : buttonAction}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        open={conflictState.isOpen}
        onOpenChange={(open) => !open && clearConflict()}
        currentVersion={currentVersion}
        latestVersion={conflictState.latestVersion}
        resourceName={t("pleadings.title.pleading")}
        onRefresh={handleRefreshPleading}
        onOverwrite={handleForceOverwritePleading}
        loading={loading}
        fieldDiffs={conflictState.fieldDiffs}
        fieldLabels={Object.fromEntries(
          Object.entries(pleadingFieldConfig.fields).map(([field, config]) => [
            field,
            t(config.label),
          ]),
        )}
      />
    </>
  );
}
